/**
 * Unit Service
 * Unit (araç/tren) işlemleri için service (mevcut projende Unit, UnitGraph, UnitDiagnostics var)
 */

import { apiClient } from '../client/apiClient';
import { ENDPOINTS, createEndpoint } from '../config/endpoints';
import {
  // Types
  Unit,
  UnitPerformance,
  UnitType,
  UnitStatus,
  Alarm,
  
  // Request/Response
  PaginatedResponse,
  
  // Query Params
  BaseQueryParams,
  ID,
  
  // Export
  ExportFormat,
  
  // Common
  DateRangeFilter
} from '../types/api.types';

/**
 * Unit query parameters
 */
import { UnitQueryParams } from '../types';

/**
 * Unit performance query
 */
export interface UnitPerformanceQuery extends DateRangeFilter {
  unitId?: ID;
  fleetId?: ID;
  tenantId?: ID;
  metrics?: string[]; // ['availability', 'reliability', 'efficiency', 'mtbf', 'mttr']
  groupBy?: 'hour' | 'day' | 'week' | 'month';
}

/**
 * Unit create/update request
 */
export interface UnitRequest {
  unitId: string;
  name: string;
  description?: string;
  type: UnitType;
  status?: UnitStatus;
  fleetId?: ID;
  manufacturer?: string;
  model?: string;
  serialNumber?: string;
  commissionDate?: Date;
  specifications?: Record<string, any>;
  metadata?: Record<string, any>;
  location?: {
    lat: number;
    lng: number;
    address?: string;
  };
}

/**
 * Unit maintenance record
 */
export interface MaintenanceRecord {
  id: ID;
  unitId: ID;
  type: 'preventive' | 'corrective' | 'predictive';
  description: string;
  performedBy: string;
  startDate: Date;
  endDate: Date;
  duration: number; // hours
  cost?: number;
  parts?: Array<{
    name: string;
    quantity: number;
    cost?: number;
  }>;
  notes?: string;
  attachments?: string[];
}

/**
 * Unit diagnostics data
 */
export interface DiagnosticsData {
  timestamp: Date;
  unitId: ID;
  metrics: Record<string, number>;
  sensors: Record<string, any>;
  healthScore: number;
  recommendations?: string[];
  warnings?: Array<{
    code: string;
    message: string;
    severity: 'info' | 'warning' | 'error';
  }>;
}

/**
 * Unit graph data point
 */
export interface GraphDataPoint {
  timestamp: Date;
  value: number;
  unit?: string;
  min?: number;
  max?: number;
  avg?: number;
}

/**
 * Unit Service Interface
 */
export interface IUnitService {
  // CRUD Operations
  getUnits(params?: UnitQueryParams): Promise<PaginatedResponse<Unit>>;
  getUnit(id: ID): Promise<Unit>;
  createUnit(unit: UnitRequest): Promise<Unit>;
  updateUnit(id: ID, unit: Partial<UnitRequest>): Promise<Unit>;
  deleteUnit(id: ID): Promise<void>;
  bulkUpdateUnits(unitIds: ID[], updates: Partial<UnitRequest>): Promise<void>;
  
  // Special Queries
  getUnitByUnitId(unitId: string): Promise<Unit>;
  getUnitsByFleet(fleetId: ID, params?: UnitQueryParams): Promise<PaginatedResponse<Unit>>;
  getUnitsByTenant(tenantId: ID, params?: UnitQueryParams): Promise<PaginatedResponse<Unit>>;
  getUnitsRequiringAttention(params?: BaseQueryParams): Promise<PaginatedResponse<Unit>>;
  getUnitsByType(type: UnitType, params?: UnitQueryParams): Promise<PaginatedResponse<Unit>>;
  getUnitsByStatus(status: UnitStatus, params?: UnitQueryParams): Promise<PaginatedResponse<Unit>>;
  searchUnits(query: string, params?: BaseQueryParams): Promise<PaginatedResponse<Unit>>;
  
  // Performance & Metrics
  getUnitPerformance(id: ID): Promise<UnitPerformance>;
  getPerformanceMetrics(query: UnitPerformanceQuery): Promise<UnitPerformance[]>;
  getPerformanceTrends(unitId: ID, dateRange: DateRangeFilter): Promise<GraphDataPoint[]>;
  getHealthScores(unitIds?: ID[]): Promise<Array<{ unitId: ID; score: number; trend: string }>>;
  getKpis(unitId: ID): Promise<Record<string, number>>;
  
  // Alarms
  getUnitAlarms(unitId: ID, params?: BaseQueryParams): Promise<PaginatedResponse<Alarm>>;
  getActiveAlarms(unitId: ID): Promise<Alarm[]>;
  getAlarmHistory(unitId: ID, dateRange: DateRangeFilter): Promise<Alarm[]>;
  getAlarmStatistics(unitId: ID): Promise<any>;
  
  // Wagons & Components
  getUnitWagons(unitId: ID): Promise<any[]>;
  getWagonDetails(unitId: ID, wagonId: ID): Promise<any>;
  getBogieDetails(unitId: ID, wagonId: ID, bogieId: ID): Promise<any>;
  getWheelDetails(unitId: ID, wagonId: ID, wheelId: ID): Promise<any>;
  
  // Diagnostics
  getDiagnostics(unitId: ID, dateRange?: DateRangeFilter): Promise<DiagnosticsData[]>;
  getRealTimeDiagnostics(unitId: ID): Promise<DiagnosticsData>;
  runDiagnostic(unitId: ID, testType: string): Promise<any>;
  getDiagnosticHistory(unitId: ID): Promise<any[]>;
  
  // Maintenance
  getMaintenanceRecords(unitId: ID): Promise<MaintenanceRecord[]>;
  getUpcomingMaintenance(unitId: ID): Promise<MaintenanceRecord[]>;
  scheduleMaintenance(unitId: ID, record: Partial<MaintenanceRecord>): Promise<MaintenanceRecord>;
  completeMaintenance(recordId: ID, data: Partial<MaintenanceRecord>): Promise<MaintenanceRecord>;
  getMaintenanceSchedule(unitId: ID): Promise<any[]>;
  
  // Graphs & Charts
  getGraphData(unitId: ID, metric: string, dateRange: DateRangeFilter): Promise<GraphDataPoint[]>;
  getMultipleGraphData(unitId: ID, metrics: string[], dateRange: DateRangeFilter): Promise<Record<string, GraphDataPoint[]>>;
  getComparativeGraphData(unitIds: ID[], metric: string, dateRange: DateRangeFilter): Promise<Record<string, GraphDataPoint[]>>;
  getRealTimeGraphData(unitId: ID, metric: string): Promise<GraphDataPoint[]>;
  
  // Export & Reports
  exportUnits(format: ExportFormat, params?: UnitQueryParams): Promise<Blob>;
  exportUnitReport(unitId: ID, format: ExportFormat): Promise<Blob>;
  exportPerformanceReport(unitId: ID, format: ExportFormat, dateRange: DateRangeFilter): Promise<Blob>;
  exportMaintenanceReport(unitId: ID, format: ExportFormat): Promise<Blob>;
  
  // Configuration & Settings
  getUnitTypes(): Promise<UnitType[]>;
  getUnitStatuses(): Promise<UnitStatus[]>;
  getUnitConfig(): Promise<any>;
  updateUnitConfig(unitId: ID, config: any): Promise<any>;
  
  // Location & Tracking
  updateUnitLocation(unitId: ID, location: { lat: number; lng: number }): Promise<void>;
  getUnitLocations(unitIds: ID[]): Promise<Array<{ unitId: ID; location: { lat: number; lng: number }; timestamp: Date }>>;
  getLocationHistory(unitId: ID, dateRange: DateRangeFilter): Promise<any[]>;
  
  // Real-time
  subscribeToUnitUpdates(unitId: ID, callback: (update: any) => void): () => void;
  getUnitStream(unitId: ID): EventSource;
}

/**
 * Unit Service Implementation
 */
export class UnitService implements IUnitService {
  private readonly unitStreams: Map<ID, EventSource> = new Map();
  private readonly subscribers: Map<ID, Array<(update: any) => void>> = new Map();

  // ==================== CRUD OPERATIONS ====================

  async getUnits(params?: UnitQueryParams): Promise<PaginatedResponse<Unit>> {
    try {
      const endpoint = ENDPOINTS.UNITS.LIST;
      const cacheKey = this.generateCacheKey('units', params);
      
      return await apiClient.getList<Unit>(endpoint, params, {
        cacheKey,
        cacheTTL: 60 * 1000, // 1 dakika
        params: this.normalizeUnitParams(params)
      });
    } catch (error) {
      console.error('Get units error:', error);
      throw error;
    }
  }

  async getUnit(id: ID): Promise<Unit> {
    try {
      const endpoint = createEndpoint(ENDPOINTS.UNITS.DETAIL, id);
      const cacheKey = `unit_${id}`;
      
      const response = await apiClient.get<Unit>(endpoint, id, {
        cacheKey,
        cacheTTL: 2 * 60 * 1000, // 2 dakika
        params: {
          includeWagons: true,
          includePerformance: true
        }
      });
      
      return response.data;
    } catch (error) {
      console.error(`Get unit ${id} error:`, error);
      throw error;
    }
  }

  async createUnit(unit: UnitRequest): Promise<Unit> {
    try {
      const endpoint = ENDPOINTS.UNITS.CREATE;
      const response = await apiClient.customPost<Unit>(endpoint, unit);
      
      // Cache'i temizle
      this.clearUnitsCache();
      
      return response.data;
    } catch (error) {
      console.error('Create unit error:', error);
      throw error;
    }
  }

  async updateUnit(id: ID, unit: Partial<UnitRequest>): Promise<Unit> {
    try {
      const endpoint = createEndpoint(ENDPOINTS.UNITS.UPDATE, id);
      const response = await apiClient.customPut<Unit>(endpoint, unit);
      
      // Cache'i temizle
      this.clearUnitCache(id);
      this.clearUnitsCache();
      
      return response.data;
    } catch (error) {
      console.error(`Update unit ${id} error:`, error);
      throw error;
    }
  }

  async deleteUnit(id: ID): Promise<void> {
    try {
      const endpoint = createEndpoint(ENDPOINTS.UNITS.DELETE, id);
      await apiClient.delete(endpoint, id);
      
      // Cache'i temizle
      this.clearUnitCache(id);
      this.clearUnitsCache();
    } catch (error) {
      console.error(`Delete unit ${id} error:`, error);
      throw error;
    }
  }

  async bulkUpdateUnits(unitIds: ID[], updates: Partial<UnitRequest>): Promise<void> {
    try {
      const endpoint = `${ENDPOINTS.UNITS.BASE}/bulk-update`;
      await apiClient.customPut<void>(endpoint, { unitIds, updates });
      
      // Cache'i temizle
      this.clearUnitsCache();
      for (const id of unitIds) {
        this.clearUnitCache(id);
      }
    } catch (error) {
      console.error('Bulk update units error:', error);
      throw error;
    }
  }

  // ==================== SPECIAL QUERIES ====================

  async getUnitByUnitId(unitId: string): Promise<Unit> {
    try {
      const endpoint = `${ENDPOINTS.UNITS.BASE}/by-unit-id/${unitId}`;
      const cacheKey = `unit_by_unit_id_${unitId}`;
      
      const response = await apiClient.customGet<Unit>(endpoint, {
        cacheKey,
        cacheTTL: 2 * 60 * 1000
      });
      
      return response.data;
    } catch (error) {
      console.error(`Get unit by unitId ${unitId} error:`, error);
      throw error;
    }
  }

  async getUnitsByFleet(fleetId: ID, params?: UnitQueryParams): Promise<PaginatedResponse<Unit>> {
    const fleetParams: UnitQueryParams = {
      ...params,
      fleetId
    };
    
    return this.getUnits(fleetParams);
  }

  async getUnitsByTenant(tenantId: ID, params?: UnitQueryParams): Promise<PaginatedResponse<Unit>> {
    const tenantParams: UnitQueryParams = {
      ...params,
      tenantId
    };
    
    return this.getUnits(tenantParams);
  }

  async getUnitsRequiringAttention(params?: BaseQueryParams): Promise<PaginatedResponse<Unit>> {
    try {
      const endpoint = ENDPOINTS.UNITS.ATTENTION_REQUIRED;
      const cacheKey = this.generateCacheKey('units_attention', params);
      
      return await apiClient.getList<Unit>(endpoint, params, {
        cacheKey,
        cacheTTL: 30 * 1000 // 30 saniye
      });
    } catch (error) {
      console.error('Get units requiring attention error:', error);
      throw error;
    }
  }

  async getUnitsByType(type: UnitType, params?: UnitQueryParams): Promise<PaginatedResponse<Unit>> {
    const typeParams: UnitQueryParams = {
      ...params,
      type
    };
    
    return this.getUnits(typeParams);
  }

  async getUnitsByStatus(status: UnitStatus, params?: UnitQueryParams): Promise<PaginatedResponse<Unit>> {
    const statusParams: UnitQueryParams = {
      ...params,
      status
    };
    
    return this.getUnits(statusParams);
  }

  async searchUnits(query: string, params?: BaseQueryParams): Promise<PaginatedResponse<Unit>> {
    try {
      const endpoint = `${ENDPOINTS.UNITS.BASE}/search`;
      const searchParams = {
        ...params,
        q: query
      };
      
      const cacheKey = this.generateCacheKey('units_search', { query, ...params });
      
      return await apiClient.getList<Unit>(endpoint, searchParams, {
        cacheKey,
        cacheTTL: 30 * 1000
      });
    } catch (error) {
      console.error('Search units error:', error);
      throw error;
    }
  }

  // ==================== PERFORMANCE & METRICS ====================

  async getUnitPerformance(id: ID): Promise<UnitPerformance> {
    try {
      const endpoint = createEndpoint(ENDPOINTS.UNITS.PERFORMANCE, id);
      const cacheKey = `unit_performance_${id}`;
      
      const response = await apiClient.customGet<UnitPerformance>(endpoint, {
        cacheKey,
        cacheTTL: 60 * 1000 // 1 dakika
      });
      
      return response.data;
    } catch (error) {
      console.error(`Get unit performance ${id} error:`, error);
      throw error;
    }
  }

  async getPerformanceMetrics(query: UnitPerformanceQuery): Promise<UnitPerformance[]> {
    try {
      const endpoint = `${ENDPOINTS.UNITS.BASE}/performance/metrics`;
      const cacheKey = this.generateCacheKey('unit_performance_metrics', query);
      
      const response = await apiClient.customGet<UnitPerformance[]>(endpoint, {
        cacheKey,
        cacheTTL: 60 * 1000,
        params: this.normalizeDateRange(query)
      });
      
      return response.data || [];
    } catch (error) {
      console.error('Get performance metrics error:', error);
      throw error;
    }
  }

  async getPerformanceTrends(unitId: ID, dateRange: DateRangeFilter): Promise<GraphDataPoint[]> {
    try {
      const endpoint = `${ENDPOINTS.UNITS.BASE}/${unitId}/performance/trends`;
      const cacheKey = `unit_performance_trends_${unitId}_${JSON.stringify(dateRange)}`;
      
      const response = await apiClient.customGet<GraphDataPoint[]>(endpoint, {
        cacheKey,
        cacheTTL: 60 * 1000,
        params: this.normalizeDateRange(dateRange)
      });
      
      return response.data || [];
    } catch (error) {
      console.error(`Get performance trends ${unitId} error:`, error);
      throw error;
    }
  }

  async getHealthScores(unitIds?: ID[]): Promise<Array<{ unitId: ID; score: number; trend: string }>> {
    try {
      const endpoint = `${ENDPOINTS.UNITS.BASE}/health-scores`;
      const cacheKey = `unit_health_scores_${unitIds?.join(',') || 'all'}`;
      
      const response = await apiClient.customGet<any>(endpoint, {
        cacheKey,
        cacheTTL: 30 * 1000,
        params: unitIds ? { unitIds: unitIds.join(',') } : undefined
      });
      
      return response.data || [];
    } catch (error) {
      console.error('Get health scores error:', error);
      throw error;
    }
  }

  async getKpis(unitId: ID): Promise<Record<string, number>> {
    try {
      const endpoint = `${ENDPOINTS.UNITS.BASE}/${unitId}/kpis`;
      const cacheKey = `unit_kpis_${unitId}`;
      
      const response = await apiClient.customGet<Record<string, number>>(endpoint, {
        cacheKey,
        cacheTTL: 30 * 1000
      });
      
      return response.data || {};
    } catch (error) {
      console.error(`Get KPIs ${unitId} error:`, error);
      throw error;
    }
  }

  // ==================== ALARMS ====================

  async getUnitAlarms(unitId: ID, params?: BaseQueryParams): Promise<PaginatedResponse<Alarm>> {
    try {
      const endpoint = createEndpoint(ENDPOINTS.UNITS.ALARMS, unitId);
      const cacheKey = this.generateCacheKey(`unit_alarms_${unitId}`, params);
      
      return await apiClient.getList<Alarm>(endpoint, params, {
        cacheKey,
        cacheTTL: 30 * 1000
      });
    } catch (error) {
      console.error(`Get unit alarms ${unitId} error:`, error);
      throw error;
    }
  }

  async getActiveAlarms(unitId: ID): Promise<Alarm[]> {
    try {
      const endpoint = `${ENDPOINTS.UNITS.BASE}/${unitId}/alarms/active`;
      const cacheKey = `unit_active_alarms_${unitId}`;
      
      const response = await apiClient.customGet<Alarm[]>(endpoint, {
        cacheKey,
        cacheTTL: 15 * 1000 // 15 saniye (active alarms sık değişir)
      });
      
      return response.data || [];
    } catch (error) {
      console.error(`Get active alarms ${unitId} error:`, error);
      throw error;
    }
  }

  async getAlarmHistory(unitId: ID, dateRange: DateRangeFilter): Promise<Alarm[]> {
    try {
      const endpoint = `${ENDPOINTS.UNITS.BASE}/${unitId}/alarms/history`;
      const cacheKey = `unit_alarm_history_${unitId}_${JSON.stringify(dateRange)}`;
      
      const response = await apiClient.customGet<Alarm[]>(endpoint, {
        cacheKey,
        cacheTTL: 60 * 1000,
        params: this.normalizeDateRange(dateRange)
      });
      
      return response.data || [];
    } catch (error) {
      console.error(`Get alarm history ${unitId} error:`, error);
      throw error;
    }
  }

  async getAlarmStatistics(unitId: ID): Promise<any> {
    try {
      const endpoint = `${ENDPOINTS.UNITS.BASE}/${unitId}/alarms/statistics`;
      const cacheKey = `unit_alarm_stats_${unitId}`;
      
      const response = await apiClient.customGet<any>(endpoint, {
        cacheKey,
        cacheTTL: 60 * 1000
      });
      
      return response.data;
    } catch (error) {
      console.error(`Get alarm statistics ${unitId} error:`, error);
      throw error;
    }
  }

  // ==================== WAGONS & COMPONENTS ====================

  async getUnitWagons(unitId: ID): Promise<any[]> {
    try {
      const endpoint = createEndpoint(ENDPOINTS.UNITS.WAGONS, unitId);
      const cacheKey = `unit_wagons_${unitId}`;
      
      const response = await apiClient.customGet<any[]>(endpoint, {
        cacheKey,
        cacheTTL: 5 * 60 * 1000 // 5 dakika (wagon bilgisi sık değişmez)
      });
      
      return response.data || [];
    } catch (error) {
      console.error(`Get unit wagons ${unitId} error:`, error);
      throw error;
    }
  }

  async getWagonDetails(unitId: ID, wagonId: ID): Promise<any> {
    try {
      const endpoint = `${ENDPOINTS.UNITS.BASE}/${unitId}/wagons/${wagonId}`;
      const cacheKey = `wagon_details_${unitId}_${wagonId}`;
      
      const response = await apiClient.customGet<any>(endpoint, {
        cacheKey,
        cacheTTL: 5 * 60 * 1000
      });
      
      return response.data;
    } catch (error) {
      console.error(`Get wagon details ${unitId}/${wagonId} error:`, error);
      throw error;
    }
  }

  async getBogieDetails(unitId: ID, wagonId: ID, bogieId: ID): Promise<any> {
    try {
      const endpoint = `${ENDPOINTS.UNITS.BASE}/${unitId}/wagons/${wagonId}/bogies/${bogieId}`;
      const cacheKey = `bogie_details_${unitId}_${wagonId}_${bogieId}`;
      
      const response = await apiClient.customGet<any>(endpoint, {
        cacheKey,
        cacheTTL: 5 * 60 * 1000
      });
      
      return response.data;
    } catch (error) {
      console.error(`Get bogie details ${unitId}/${wagonId}/${bogieId} error:`, error);
      throw error;
    }
  }

  async getWheelDetails(unitId: ID, wagonId: ID, wheelId: ID): Promise<any> {
    try {
      const endpoint = `${ENDPOINTS.UNITS.BASE}/${unitId}/wagons/${wagonId}/wheels/${wheelId}`;
      const cacheKey = `wheel_details_${unitId}_${wagonId}_${wheelId}`;
      
      const response = await apiClient.customGet<any>(endpoint, {
        cacheKey,
        cacheTTL: 5 * 60 * 1000
      });
      
      return response.data;
    } catch (error) {
      console.error(`Get wheel details ${unitId}/${wagonId}/${wheelId} error:`, error);
      throw error;
    }
  }

  // ==================== DIAGNOSTICS ====================

  async getDiagnostics(unitId: ID, dateRange?: DateRangeFilter): Promise<DiagnosticsData[]> {
    try {
      const endpoint = createEndpoint(ENDPOINTS.UNITS.DIAGNOSTICS, unitId);
      const cacheKey = `unit_diagnostics_${unitId}_${JSON.stringify(dateRange || {})}`;
      
      const response = await apiClient.customGet<DiagnosticsData[]>(endpoint, {
        cacheKey,
        cacheTTL: 30 * 1000,
        params: dateRange ? this.normalizeDateRange(dateRange) : undefined
      });
      
      return response.data || [];
    } catch (error) {
      console.error(`Get diagnostics ${unitId} error:`, error);
      throw error;
    }
  }

  async getRealTimeDiagnostics(unitId: ID): Promise<DiagnosticsData> {
    try {
      const endpoint = `${ENDPOINTS.UNITS.BASE}/${unitId}/diagnostics/realtime`;
      
      const response = await apiClient.customGet<DiagnosticsData>(endpoint, {
        cacheTTL: 0, // No cache for real-time data
        skipCache: true
      });
      
      return response.data;
    } catch (error) {
      console.error(`Get real-time diagnostics ${unitId} error:`, error);
      throw error;
    }
  }

  async runDiagnostic(unitId: ID, testType: string): Promise<any> {
    try {
      const endpoint = `${ENDPOINTS.UNITS.BASE}/${unitId}/diagnostics/run`;
      
      const response = await apiClient.customPost<any>(endpoint, { testType });
      
      return response.data;
    } catch (error) {
      console.error(`Run diagnostic ${unitId} error:`, error);
      throw error;
    }
  }

  async getDiagnosticHistory(unitId: ID): Promise<any[]> {
    try {
      const endpoint = `${ENDPOINTS.UNITS.BASE}/${unitId}/diagnostics/history`;
      const cacheKey = `diagnostic_history_${unitId}`;
      
      const response = await apiClient.customGet<any[]>(endpoint, {
        cacheKey,
        cacheTTL: 60 * 1000
      });
      
      return response.data || [];
    } catch (error) {
      console.error(`Get diagnostic history ${unitId} error:`, error);
      throw error;
    }
  }

  // ==================== MAINTENANCE ====================

  async getMaintenanceRecords(unitId: ID): Promise<MaintenanceRecord[]> {
    try {
      const endpoint = `${ENDPOINTS.UNITS.BASE}/${unitId}/maintenance`;
      const cacheKey = `maintenance_records_${unitId}`;
      
      const response = await apiClient.customGet<MaintenanceRecord[]>(endpoint, {
        cacheKey,
        cacheTTL: 60 * 1000
      });
      
      return response.data || [];
    } catch (error) {
      console.error(`Get maintenance records ${unitId} error:`, error);
      throw error;
    }
  }

  async getUpcomingMaintenance(unitId: ID): Promise<MaintenanceRecord[]> {
    try {
      const endpoint = `${ENDPOINTS.UNITS.BASE}/${unitId}/maintenance/upcoming`;
      const cacheKey = `upcoming_maintenance_${unitId}`;
      
      const response = await apiClient.customGet<MaintenanceRecord[]>(endpoint, {
        cacheKey,
        cacheTTL: 60 * 1000
      });
      
      return response.data || [];
    } catch (error) {
      console.error(`Get upcoming maintenance ${unitId} error:`, error);
      throw error;
    }
  }

  async scheduleMaintenance(unitId: ID, record: Partial<MaintenanceRecord>): Promise<MaintenanceRecord> {
    try {
      const endpoint = `${ENDPOINTS.UNITS.BASE}/${unitId}/maintenance/schedule`;
      
      const response = await apiClient.customPost<MaintenanceRecord>(endpoint, record);
      
      // Cache'i temizle
      this.clearMaintenanceCache(unitId);
      
      return response.data;
    } catch (error) {
      console.error(`Schedule maintenance ${unitId} error:`, error);
      throw error;
    }
  }

  async completeMaintenance(recordId: ID, data: Partial<MaintenanceRecord>): Promise<MaintenanceRecord> {
    try {
      const endpoint = `${ENDPOINTS.UNITS.BASE}/maintenance/${recordId}/complete`;
      
      const response = await apiClient.customPut<MaintenanceRecord>(endpoint, data);
      
      // Cache'i temizle (unitId'yi response'dan al veya başka yöntemle)
      // this.clearMaintenanceCache(unitId);
      
      return response.data;
    } catch (error) {
      console.error(`Complete maintenance ${recordId} error:`, error);
      throw error;
    }
  }

  async getMaintenanceSchedule(unitId: ID): Promise<any[]> {
    try {
      const endpoint = `${ENDPOINTS.UNITS.BASE}/${unitId}/maintenance/schedule`;
      const cacheKey = `maintenance_schedule_${unitId}`;
      
      const response = await apiClient.customGet<any[]>(endpoint, {
        cacheKey,
        cacheTTL: 60 * 1000
      });
      
      return response.data || [];
    } catch (error) {
      console.error(`Get maintenance schedule ${unitId} error:`, error);
      throw error;
    }
  }

  // ==================== GRAPHS & CHARTS ====================

  async getGraphData(unitId: ID, metric: string, dateRange: DateRangeFilter): Promise<GraphDataPoint[]> {
    try {
      const endpoint = createEndpoint(ENDPOINTS.UNITS.GRAPHS, unitId);
      const cacheKey = `graph_data_${unitId}_${metric}_${JSON.stringify(dateRange)}`;
      
      const response = await apiClient.customGet<GraphDataPoint[]>(endpoint, {
        cacheKey,
        cacheTTL: 60 * 1000,
        params: {
          metric,
          ...this.normalizeDateRange(dateRange)
        }
      });
      
      return response.data || [];
    } catch (error) {
      console.error(`Get graph data ${unitId}/${metric} error:`, error);
      throw error;
    }
  }

  async getMultipleGraphData(unitId: ID, metrics: string[], dateRange: DateRangeFilter): Promise<Record<string, GraphDataPoint[]>> {
    try {
      const endpoint = `${ENDPOINTS.UNITS.BASE}/${unitId}/graphs/multiple`;
      const cacheKey = `multi_graph_data_${unitId}_${metrics.join('_')}_${JSON.stringify(dateRange)}`;
      
      const response = await apiClient.customPost<Record<string, GraphDataPoint[]>>(endpoint, {
        metrics,
        ...dateRange
      }, {
        cacheKey,
        cacheTTL: 60 * 1000
      });
      
      return response.data || {};
    } catch (error) {
      console.error(`Get multiple graph data ${unitId} error:`, error);
      throw error;
    }
  }

  async getComparativeGraphData(unitIds: ID[], metric: string, dateRange: DateRangeFilter): Promise<Record<string, GraphDataPoint[]>> {
    try {
      const endpoint = `${ENDPOINTS.UNITS.BASE}/graphs/comparative`;
      const cacheKey = `comparative_graph_${unitIds.join('_')}_${metric}_${JSON.stringify(dateRange)}`;
      
      const response = await apiClient.customPost<Record<string, GraphDataPoint[]>>(endpoint, {
        unitIds,
        metric,
        ...dateRange
      }, {
        cacheKey,
        cacheTTL: 60 * 1000
      });
      
      return response.data || {};
    } catch (error) {
      console.error('Get comparative graph data error:', error);
      throw error;
    }
  }

  async getRealTimeGraphData(unitId: ID, metric: string): Promise<GraphDataPoint[]> {
    try {
      const endpoint = `${ENDPOINTS.UNITS.BASE}/${unitId}/graphs/realtime`;
      
      const response = await apiClient.customGet<GraphDataPoint[]>(endpoint, {
        cacheTTL: 0,
        skipCache: true,
        params: { metric }
      });
      
      return response.data || [];
    } catch (error) {
      console.error(`Get real-time graph data ${unitId}/${metric} error:`, error);
      throw error;
    }
  }

  // ==================== EXPORT & REPORTS ====================

  async exportUnits(format: ExportFormat, params?: UnitQueryParams): Promise<Blob> {
    try {
      const endpoint = createEndpoint(ENDPOINTS.UNITS.EXPORT, 'all');
      return await apiClient.exportData(endpoint, format, params);
    } catch (error) {
      console.error('Export units error:', error);
      throw error;
    }
  }

  async exportUnitReport(unitId: ID, format: ExportFormat): Promise<Blob> {
    try {
      const endpoint = ENDPOINTS.UNITS.EXPORTALL;
      return await apiClient.exportData(endpoint, format);
    } catch (error) {
      console.error(`Export unit report ${unitId} error:`, error);
      throw error;
    }
  }

  async exportPerformanceReport(unitId: ID, format: ExportFormat, dateRange: DateRangeFilter): Promise<Blob> {
    try {
      const endpoint = `${ENDPOINTS.UNITS.BASE}/${unitId}/performance/export`;
      return await apiClient.exportData(endpoint, format, dateRange);
    } catch (error) {
      console.error(`Export performance report ${unitId} error:`, error);
      throw error;
    }
  }

  async exportMaintenanceReport(unitId: ID, format: ExportFormat): Promise<Blob> {
    try {
      const endpoint = `${ENDPOINTS.UNITS.BASE}/${unitId}/maintenance/export`;
      return await apiClient.exportData(endpoint, format);
    } catch (error) {
      console.error(`Export maintenance report ${unitId} error:`, error);
      throw error;
    }
  }

  // ==================== CONFIGURATION & SETTINGS ====================

  async getUnitTypes(): Promise<UnitType[]> {
    try {
      const endpoint = `${ENDPOINTS.UNITS.BASE}/types`;
      const cacheKey = 'unit_types';
      
      const response = await apiClient.customGet<UnitType[]>(endpoint, {
        cacheKey,
        cacheTTL: 60 * 60 * 1000 // 1 saat
      });
      
      return response.data || [];
    } catch (error) {
      console.error('Get unit types error:', error);
      throw error;
    }
  }

  async getUnitStatuses(): Promise<UnitStatus[]> {
    try {
      const endpoint = `${ENDPOINTS.UNITS.BASE}/statuses`;
      const cacheKey = 'unit_statuses';
      
      const response = await apiClient.customGet<UnitStatus[]>(endpoint, {
        cacheKey,
        cacheTTL: 60 * 60 * 1000
      });
      
      return response.data || [];
    } catch (error) {
      console.error('Get unit statuses error:', error);
      throw error;
    }
  }

  async getUnitConfig(): Promise<any> {
    try {
      const endpoint = `${ENDPOINTS.UNITS.BASE}/config`;
      const cacheKey = 'unit_config';
      
      const response = await apiClient.customGet<any>(endpoint, {
        cacheKey,
        cacheTTL: 30 * 60 * 1000 // 30 dakika
      });
      
      return response.data;
    } catch (error) {
      console.error('Get unit config error:', error);
      throw error;
    }
  }

  async updateUnitConfig(unitId: ID, config: any): Promise<any> {
    try {
      const endpoint = `${ENDPOINTS.UNITS.BASE}/${unitId}/config`;
      
      const response = await apiClient.customPut<any>(endpoint, config);
      
      // Cache'i temizle
      this.clearUnitCache(unitId);
      
      return response.data;
    } catch (error) {
      console.error(`Update unit config ${unitId} error:`, error);
      throw error;
    }
  }

  // ==================== LOCATION & TRACKING ====================

  async updateUnitLocation(unitId: ID, location: { lat: number; lng: number }): Promise<void> {
    try {
      const endpoint = `${ENDPOINTS.UNITS.BASE}/${unitId}/location`;
      
      await apiClient.customPut<void>(endpoint, location);
      
      // Cache'i temizle
      this.clearUnitCache(unitId);
    } catch (error) {
      console.error(`Update unit location ${unitId} error:`, error);
      throw error;
    }
  }

  async getUnitLocations(unitIds: ID[]): Promise<Array<{ unitId: ID; location: { lat: number; lng: number }; timestamp: Date }>> {
    try {
      const endpoint = `${ENDPOINTS.UNITS.BASE}/locations`;
      const cacheKey = `unit_locations_${unitIds.join(',')}`;
      
      const response = await apiClient.customPost<any>(endpoint, { unitIds }, {
        cacheKey,
        cacheTTL: 30 * 1000 // 30 saniye (location sık değişir)
      });
      
      return response.data || [];
    } catch (error) {
      console.error('Get unit locations error:', error);
      throw error;
    }
  }

  async getLocationHistory(unitId: ID, dateRange: DateRangeFilter): Promise<any[]> {
    try {
      const endpoint = `${ENDPOINTS.UNITS.BASE}/${unitId}/location/history`;
      const cacheKey = `location_history_${unitId}_${JSON.stringify(dateRange)}`;
      
      const response = await apiClient.customGet<any[]>(endpoint, {
        cacheKey,
        cacheTTL: 60 * 1000,
        params: this.normalizeDateRange(dateRange)
      });
      
      return response.data || [];
    } catch (error) {
      console.error(`Get location history ${unitId} error:`, error);
      throw error;
    }
  }

  // ==================== REAL-TIME ====================

  subscribeToUnitUpdates(unitId: ID, callback: (update: any) => void): () => void {
    if (!this.subscribers.has(unitId)) {
      this.subscribers.set(unitId, []);
    }
    
    const unitSubscribers = this.subscribers.get(unitId)!;
    unitSubscribers.push(callback);
    
    // İlk subscriber ise stream'i başlat
    if (unitSubscribers.length === 1) {
      this.startUnitStream(unitId);
    }
    
    // Unsubscribe function
    return () => {
      const subscribers = this.subscribers.get(unitId);
      if (subscribers) {
        const index = subscribers.indexOf(callback);
        if (index > -1) {
          subscribers.splice(index, 1);
        }
        
        // Son subscriber ise stream'i durdur
        if (subscribers.length === 0) {
          this.stopUnitStream(unitId);
          this.subscribers.delete(unitId);
        }
      }
    };
  }

  getUnitStream(unitId: ID): EventSource {
    const streamUrl = `${ENDPOINTS.UNITS.BASE}/${unitId}/stream`;
    return new EventSource(streamUrl);
  }

  // ==================== PRIVATE METHODS ====================

  private normalizeUnitParams(params?: UnitQueryParams): any {
    if (!params) return {};
    
    const normalized: any = { ...params };
    
    // Array'leri string'e çevir
    if (Array.isArray(normalized.type)) {
      normalized.type = normalized.type.join(',');
    }
    
    if (Array.isArray(normalized.status)) {
      normalized.status = normalized.status.join(',');
    }
    
    if (Array.isArray(normalized.fleetId)) {
      normalized.fleetId = normalized.fleetId.join(',');
    }
    
    // Date'leri ISO string'e çevir
    if (normalized.lastMaintenanceBefore instanceof Date) {
      normalized.lastMaintenanceBefore = normalized.lastMaintenanceBefore.toISOString();
    }
    
    if (normalized.lastMaintenanceAfter instanceof Date) {
      normalized.lastMaintenanceAfter = normalized.lastMaintenanceAfter.toISOString();
    }
    
    // Location objesini string'e çevir
    if (normalized.location) {
      normalized.location = `${normalized.location.lat},${normalized.location.lng},${normalized.location.radius}`;
    }
    
    // Boolean'ları string'e çevir
    if (typeof normalized.includeWagons === 'boolean') {
      normalized.includeWagons = normalized.includeWagons.toString();
    }
    
    if (typeof normalized.includePerformance === 'boolean') {
      normalized.includePerformance = normalized.includePerformance.toString();
    }
    
    if (typeof normalized.inService === 'boolean') {
      normalized.inService = normalized.inService.toString();
    }
    
    // Undefined'leri temizle
    for (const key of Object.keys(normalized)) {
      if (normalized[key] === undefined || normalized[key] === null) {
        delete normalized[key];
      }
    }
    
    return normalized;
  }

  private normalizeDateRange(dateRange: DateRangeFilter): any {
    const normalized: any = {};
    
    if (dateRange.startDate) {
      normalized.startDate = dateRange.startDate instanceof Date 
        ? dateRange.startDate.toISOString()
        : dateRange.startDate;
    }
    
    if (dateRange.endDate) {
      normalized.endDate = dateRange.endDate instanceof Date
        ? dateRange.endDate.toISOString()
        : dateRange.endDate;
    }
    
    return normalized;
  }

  private generateCacheKey(prefix: string, params?: any): string {
    if (!params) return prefix;
    
    const paramString = JSON.stringify(params);
    const hash = this.hashString(paramString);
    return `${prefix}_${hash}`;
  }

  private hashString(str: string): string {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.codePointAt(i);
      if(char !== undefined) {
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash;
      }
    }
    return Math.abs(hash).toString(36);
  }

  private clearUnitCache(unitId: ID): void {
    apiClient.cancelRequest(`unit_${unitId}`);
  }

  private clearUnitsCache(): void {
    apiClient.cancelRequest('units');
  }

  private clearMaintenanceCache(unitId: ID): void {
    apiClient.cancelRequest(`maintenance_${unitId}`);
  }

  private startUnitStream(unitId: ID): void {
    try {
      const stream = this.getUnitStream(unitId);
      this.unitStreams.set(unitId, stream);
      
      stream.onmessage = (event) => {
        try {
          const update = JSON.parse(event.data);
          this.notifyUnitSubscribers(unitId, update);
        } catch (error) {
          console.error(`Failed to parse unit ${unitId} stream message:`, error);
        }
      };
      
      stream.onerror = (error) => {
        console.error(`Unit ${unitId} stream error:`, error);
        // Reconnect logic eklenebilir
      };
      
    } catch (error) {
      console.error(`Failed to start unit ${unitId} stream:`, error);
    }
  }

  private stopUnitStream(unitId: ID): void {
    const stream = this.unitStreams.get(unitId);
    if (stream) {
      stream.close();
      this.unitStreams.delete(unitId);
    }
  }

  private notifyUnitSubscribers(unitId: ID, update: any): void {
    const subscribers = this.subscribers.get(unitId);
    if (subscribers) {
      for (const callback of subscribers) {
        try {
          callback(update);
        } catch (error) {
          console.error(`Unit ${unitId} subscriber error:`, error);
        }
      }
    }
  }
}

// Singleton instance
export const unitService = new UnitService();

// Default export
export default unitService;